import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  LinearProgress, ListSubheader, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Theme, ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha,
} from '@mui/material';
import {
  UploadFileOutlined as UploadIcon, CheckRounded as CheckIcon, ExpandMoreRounded as ExpandIcon,
  AttachFileRounded as AttachIcon, CloseRounded as RemoveIcon, HelpOutlineRounded as HelpIcon,
} from '@mui/icons-material';
import PayrollService, {
  CommonSetup, PaySetup, PayTemplate, RunDetail, StatementPreview, describeError,
} from '../../api/services/payroll.service';
import ToneChip from '../../components/ui/ToneChip';
import { status as statusTokens } from '../../theme';
import {
  Avatar, count, money, shortDate, weekLabel,
} from './payrollUI';

// Bringing statements in: one week or a stack of them.
//
// Choose the files, check what the server read, then save. Nothing is
// written until the last step, and the check screen is the whole point:
// which week each file is and what saving it does, whether the rows add
// up, and who on the statements is not in the company. Each unknown driver
// is decided once for every file they appear in: matched to somebody, added
// as a new person (a former driver needs no email), or skipped.
//
// Saving creates the new people first, then the weeks oldest first, each on
// its own: one bad file does not stop the rest.
//
// Each week can carry its weekly service report too. Both files are then
// filed under Payroll in Files, after the week saves — a file that will not
// store is reported against its week but never costs the week its pay.

type Step = 'pick' | 'checking' | 'check' | 'saving' | 'done';

interface FileItem {
  key: string;
  fileName: string;
  /** The statement itself, filed in Files once the week is saved. */
  file: File;
  /** The week's weekly service report, if the owner has one. */
  weeklyService: File | null;
  text: string;
  preview: StatementPreview | null;
  error: string | null;
  /** The week is finalized on file; replace it anyway. */
  replace: boolean;
}

type Decision =
  | { kind: 'match'; accountId: string; name: string }
  | { kind: 'skip' }
  | {
    kind: 'new'; firstName: string; lastName: string; status: 'former' | 'current';
    email: string; phone: string; setup: string;
  };

interface Result {
  fileName: string; weekEnd: string | null; detail: RunDetail | null; error: string | null;
  /** The week saved but its files did not make it into Files. */
  filesError?: string | null;
}

// "Rauel Wilson" → Rauel / Wilson; "WILSON, RAUEL" → Rauel / Wilson.
function splitName(raw: string): { firstName: string; lastName: string } {
  const title = (s: string) => s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
  const s = raw.trim().replace(/\s+/g, ' ');
  if (s.includes(',')) {
    const [last, first] = s.split(',').map((x) => x.trim());
    return { firstName: title(first || ''), lastName: title(last || '') };
  }
  const parts = s.split(' ');
  return { firstName: title(parts[0] || ''), lastName: title(parts.slice(1).join(' ')) };
}

/** What each file is, on hover — the screen itself stays two lines. */
const Help: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} placement="top" arrow>
    <HelpIcon aria-label={title} tabIndex={0} sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', verticalAlign: 'text-bottom', ml: 0.5 }} />
  </Tooltip>
);

// Dropping a file needs the drag to be accepted on the way in.
const acceptDrag = (e: React.DragEvent) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); };

/**
 * One file to choose or drop. `main` is the statement — what pay is
 * calculated from — so it is the big target; the extra file is a smaller
 * row under it, still a drop target of its own (people drop the weekly
 * service as often as the statement).
 */
export const FileSlot: React.FC<{
  label: string;
  help: string;
  chosen?: string;
  note?: string;
  main?: boolean;
  optional?: boolean;
  disabled?: boolean;
  onChoose: () => void;
  onDropFiles?: (files: FileList) => void;
  onRemove?: () => void;
}> = ({ label, help, chosen, note, main, optional = !main, disabled, onChoose, onDropFiles, onRemove }) => {
  const [over, setOver] = useState(false);
  const droppable = !!onDropFiles && !disabled;
  return (
  <Stack
    direction={main ? 'column' : 'row'} alignItems="center" gap={main ? 1.25 : 1.5}
    data-dragover={over || undefined}
    onDragOver={droppable ? (e) => { acceptDrag(e); setOver(true); } : undefined}
    onDragLeave={droppable ? () => setOver(false) : undefined}
    onDrop={droppable ? (e) => { e.preventDefault(); e.stopPropagation(); setOver(false); if (e.dataTransfer.files.length) onDropFiles!(e.dataTransfer.files); } : undefined}
    sx={main ? {
      border: '2px dashed', borderColor: over || chosen ? 'primary.main' : 'primary.light', borderRadius: 3,
      px: 3, py: 4, textAlign: 'center', bgcolor: over ? (t: Theme) => alpha(t.palette.primary.main, 0.08) : 'action.hover',
      transition: 'border-color 120ms, background-color 120ms',
    } : {
      border: droppable ? '2px dashed' : '1px solid', borderColor: over || chosen ? 'primary.main' : droppable ? 'primary.light' : 'divider',
      borderRadius: 2, px: 2, py: 1.25, bgcolor: over ? (t: Theme) => alpha(t.palette.primary.main, 0.08) : 'transparent',
      transition: 'border-color 120ms, background-color 120ms',
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
      <Typography variant={main ? 'subtitle1' : 'body2'} sx={{ fontWeight: main ? 700 : 600 }}>
        {label}
        {optional && <Typography component="span" variant="caption" color="text.secondary"> (optional)</Typography>}
        <Help title={help} />
      </Typography>
      <Typography variant={main ? 'body2' : 'caption'} color={chosen ? 'text.primary' : 'text.secondary'} noWrap sx={{ display: 'block' }}>
        {chosen || note || (main || droppable ? 'Drop it here or choose it' : 'No file chosen')}
      </Typography>
    </Box>
    <Stack direction="row" alignItems="center" gap={0.5}>
      {chosen && onRemove && (
        <IconButton size="small" aria-label={`Remove the ${label.toLowerCase()}`} onClick={onRemove} disabled={disabled}>
          <RemoveIcon fontSize="small" />
        </IconButton>
      )}
      <Button
        variant={main && !chosen ? 'contained' : 'outlined'} disableElevation size={main ? 'medium' : 'small'}
        startIcon={<UploadIcon />} disabled={disabled} onClick={onChoose}
        aria-label={`Choose ${label.toLowerCase()}`}
      >
        {chosen ? 'Change' : 'Choose'}
      </Button>
    </Stack>
  </Stack>
  );
};

const UploadStatementDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onUploaded: (detail: RunDetail) => void;
}> = ({ open, onClose, onUploaded }) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const pickServiceInput = useRef<HTMLInputElement>(null);
  const serviceInput = useRef<HTMLInputElement>(null);
  /** Chosen on the first screen, read on Continue. */
  const [picked, setPicked] = useState<{ statements: File[]; weeklyService: File | null }>({ statements: [], weeklyService: null });
  /** Which week the weekly-service picker is choosing for. */
  const [serviceFor, setServiceFor] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('pick');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [templates, setTemplates] = useState<{ saved: PayTemplate[]; common: CommonSetup[] }>({ saved: [], common: [] });
  const [bulkSetup, setBulkSetup] = useState('');
  const [showMatched, setShowMatched] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; label: string }>({ done: 0, total: 0, label: '' });
  const [results, setResults] = useState<Result[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) PayrollService.templates().then(setTemplates).catch(() => undefined);
  }, [open]);

  const reset = () => {
    setStep('pick'); setFiles([]); setPicked({ statements: [], weeklyService: null });
    setDecisions({}); setBulkSetup(''); setShowMatched(false);
    setResults([]); setError(null); setProgress({ done: 0, total: 0, label: '' });
  };
  const close = () => { reset(); onClose(); };

  // Statements are CSVs. A statement and a PDF dropped together onto the
  // big target are the week and its weekly service: each goes to its slot.
  const pickStatements = (list: FileList | null) => {
    const all = Array.from(list || []);
    const statements = all.filter((f) => /\.csv$/i.test(f.name));
    const others = all.filter((f) => !/\.csv$/i.test(f.name));
    if (statements.length) setPicked((p) => ({ ...p, statements }));
    if (statements.length === 1 && others.length === 1) setPicked((p) => ({ ...p, weeklyService: others[0] }));
    if (fileInput.current) fileInput.current.value = '';
  };

  // One weekly service goes with one week; several weeks get theirs per
  // row on the next step.
  const handleFiles = async ({ statements: chosen, weeklyService }: { statements: File[]; weeklyService: File | null }) => {
    const picked = chosen;
    if (!picked.length) return;
    setError(null);
    setStep('checking');
    setProgress({ done: 0, total: picked.length, label: 'Reading' });
    const items: FileItem[] = [];
    // A few at a time: each statement is its own request.
    let next = 0;
    let done = 0;
    const worker = async () => {
      while (next < picked.length) {
        const file = picked[next];
        const key = `${next}-${file.name}`;
        next += 1;
        const text = await file.text();
        try {
          items.push({ key, fileName: file.name, file, weeklyService: null, text, preview: await PayrollService.previewStatement(text, file.name), error: null, replace: false });
        } catch (err) {
          items.push({ key, fileName: file.name, file, weeklyService: null, text, preview: null, error: describeError(err, 'Not a weekly statement.'), replace: false });
        }
        done += 1;
        setProgress({ done, total: picked.length, label: 'Reading' });
      }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
    // Oldest week first, unreadable files last.
    items.sort((a, b) => (a.preview && b.preview ? a.preview.week.weekEnd.localeCompare(b.preview.week.weekEnd) : a.preview ? -1 : b.preview ? 1 : 0));
    if (items.length === 1 && weeklyService) items[0].weeklyService = weeklyService;
    setFiles(items);
    setStep('check');
  };

  // What saving does with each file.
  const plan = useMemo(() => {
    const seen = new Set<string>();
    return files.map((f) => {
      if (!f.preview) return { file: f, action: 'unreadable' as const };
      const week = f.preview.week.weekEnd;
      if (seen.has(week)) return { file: f, action: 'duplicate' as const };
      seen.add(week);
      if (f.preview.existing?.status === 'finalized' && !f.replace) return { file: f, action: 'finalized' as const };
      return { file: f, action: f.preview.existing ? 'replace' as const : 'new' as const };
    });
  }, [files]);
  const toSave = plan.filter((p) => p.action === 'new' || p.action === 'replace').map((p) => p.file);

  // Every driver nobody matched, once, across the files that will be saved.
  const unknown = useMemo(() => {
    const byId = new Map<string, { fedexId: string; name: string; weeks: number; stops: number; suggestions: { accountId: string; name: string; likely: boolean }[] }>();
    for (const f of toSave) {
      for (const d of f.preview!.drivers) {
        if (d.match) continue;
        const u = byId.get(d.fedexId) || { fedexId: d.fedexId, name: d.name, weeks: 0, stops: 0, suggestions: d.suggestions };
        u.weeks += 1;
        u.stops += d.totals.stops;
        byId.set(d.fedexId, u);
      }
    }
    return Array.from(byId.values()).sort((a, b) => b.weeks - a.weeks || a.name.localeCompare(b.name));
  }, [toSave]);
  const matched = useMemo(() => {
    const byId = new Map<string, { fedexId: string; statementName: string; name: string }>();
    for (const f of toSave) for (const d of f.preview!.drivers) if (d.match) byId.set(d.fedexId, { fedexId: d.fedexId, statementName: d.name, name: d.match.name });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [toSave]);
  // Everybody the pickers can choose: every suggestion for any row.
  const everyone = useMemo(() => {
    const seen = new Map<string, string>();
    for (const u of unknown) for (const s of u.suggestions) seen.set(s.accountId, s.name);
    return Array.from(seen.entries()).map(([accountId, name]) => ({ accountId, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [unknown]);

  const setups: { value: string; label: string; setup: PaySetup }[] = [
    ...templates.saved.map((t) => ({ value: `t:${t.id}`, label: t.name, setup: t })),
    ...templates.common.map((c, i) => ({ value: `c:${i}`, label: c.description, setup: c })),
  ];
  const newDecision = (u: { name: string }, status: 'former' | 'current' = 'former', setup = ''): Decision => ({
    kind: 'new', ...splitName(u.name), status, email: '', phone: '', setup,
  });
  const decide = (fedexId: string, d: Decision | null) => setDecisions((m) => {
    const next = { ...m };
    if (d) next[fedexId] = d; else delete next[fedexId];
    return next;
  });
  const patchNew = (fedexId: string, patch: Partial<Extract<Decision, { kind: 'new' }>>) => setDecisions((m) => {
    const cur = m[fedexId];
    return cur && cur.kind === 'new' ? { ...m, [fedexId]: { ...cur, ...patch } } : m;
  });
  const undecided = unknown.filter((u) => !decisions[u.fedexId]);
  const incompleteNew = unknown.filter((u) => {
    const d = decisions[u.fedexId];
    return d?.kind === 'new' && (!d.firstName.trim() || !d.lastName.trim());
  });

  const save = async () => {
    setStep('saving');
    setError(null);
    const matches: Record<string, string> = {};
    // 1. New people first, so every file can match them.
    const creating = unknown.filter((u) => decisions[u.fedexId]?.kind === 'new');
    const total = creating.length + toSave.length;
    let done = 0;
    for (const u of creating) {
      const d = decisions[u.fedexId] as Extract<Decision, { kind: 'new' }>;
      setProgress({ done, total, label: `Adding ${d.firstName} ${d.lastName}` });
      const setup = setups.find((x) => x.value === d.setup)?.setup;
      try {
        const person = await PayrollService.addPerson({
          firstName: d.firstName.trim(),
          lastName: d.lastName.trim(),
          status: d.status,
          fedexId: u.fedexId,
          ...(d.status === 'current' && d.email.trim() && { email: d.email.trim() }),
          ...(d.status === 'current' && d.phone.trim() && { phone: d.phone.trim() }),
          ...(setup && { rules: setup.rules, daysThatCount: setup.daysThatCount }),
          ...(setup?.stopKinds?.length && { stopKinds: setup.stopKinds }),
        });
        // Remembered as a match, so a retry does not add them twice.
        decide(u.fedexId, { kind: 'match', accountId: person.accountId, name: person.name });
        matches[u.fedexId] = person.accountId;
      } catch (err) {
        setError(`${d.firstName} ${d.lastName}: ${describeError(err, 'could not be added.')}`);
        setStep('check');
        return;
      }
      done += 1;
    }
    for (const u of unknown) {
      const d = decisions[u.fedexId];
      if (d?.kind === 'match') matches[u.fedexId] = d.accountId;
      else if (!matches[u.fedexId]) matches[u.fedexId] = 'skip';
    }
    // 2. The weeks, oldest first, each on its own.
    const out: Result[] = [];
    for (const f of toSave) {
      setProgress({ done, total, label: `Saving the week ending ${shortDate(f.preview!.week.weekEnd)}` });
      try {
        const detail = await PayrollService.commitStatement({ text: f.text, fileName: f.fileName, matches, replaceFinalized: f.replace });
        let filesError: string | null = null;
        try {
          await PayrollService.attachFiles(detail.run._id, { statement: f.file, weeklyService: f.weeklyService });
        } catch (err) {
          filesError = describeError(err, 'The files could not be saved in Files.');
        }
        out.push({ fileName: f.fileName, weekEnd: f.preview!.week.weekEnd, detail, error: null, filesError });
      } catch (err) {
        out.push({ fileName: f.fileName, weekEnd: f.preview!.week.weekEnd, detail: null, error: describeError(err, 'Could not be saved.') });
      }
      done += 1;
    }
    setProgress({ done, total, label: '' });
    // One week, saved: straight to it, as before — unless its files need
    // saying something about.
    if (out.length === 1 && out[0].detail && !out[0].filesError) {
      reset();
      onUploaded(out[0].detail);
      return;
    }
    setResults(out);
    setStep('done');
  };

  const newest = [...results].reverse().find((r) => r.detail)?.detail || null;
  const finish = () => { const d = newest; reset(); if (d) onUploaded(d); else onClose(); };

  const ACTION: Record<typeof plan[number]['action'], { tone: 'ok' | 'info' | 'warn' | 'neutral' | 'danger'; label: string }> = {
    new: { tone: 'ok', label: 'New week' },
    replace: { tone: 'info', label: 'Replaces the draft' },
    finalized: { tone: 'warn', label: 'Finalized, skipped' },
    duplicate: { tone: 'neutral', label: 'Same week again, skipped' },
    unreadable: { tone: 'danger', label: 'Not a statement' },
  };

  const title = step === 'done' ? 'Statements saved'
    : step === 'check' || step === 'saving' ? `Check ${files.length === 1 ? 'the statement' : `${files.length} statements`} before saving`
      : 'Upload weekly statements';

  return (
    <Dialog open={open} onClose={step === 'saving' || step === 'checking' ? undefined : close} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {(step === 'pick' || step === 'checking') && (
          <Stack gap={2}>
            <input ref={fileInput} type="file" accept=".csv,text/csv" multiple hidden onChange={(e) => pickStatements(e.target.files)} />
            <input
              ref={pickServiceInput} type="file" hidden accept=".pdf,.csv,.xlsx,.xls,.jpg,.jpeg,.png,.heic"
              onChange={(e) => {
                const service = e.target.files?.[0] || null;
                if (service) setPicked((p) => ({ ...p, weeklyService: service }));
                e.target.value = '';
              }}
            />
            {/* Two slots, the second optional, each its own drop target. */}
            <Stack gap={1.5}>
              <FileSlot
                main
                label="Statement"
                help={`GF_STATEMENT_DETAILS_ISP.csv from MyBizAccount. Pay is calculated from it. Drop as many weeks as you like.`}
                chosen={picked.statements.length > 1 ? `${picked.statements.length} files` : picked.statements[0]?.name}
                disabled={step === 'checking'}
                onChoose={() => fileInput.current?.click()}
                onDropFiles={pickStatements}
              />
              <FileSlot
                label="Weekly service"
                help="FedEx's weekly service report for the same week. Not used for pay; kept with the statement under Payroll in Files."
                chosen={picked.statements.length > 1 ? undefined : picked.weeklyService?.name}
                note={picked.statements.length > 1 ? 'Attach per week on the next step' : undefined}
                disabled={step === 'checking' || picked.statements.length > 1}
                onChoose={() => pickServiceInput.current?.click()}
                onDropFiles={(list) => setPicked((p) => ({ ...p, weeklyService: list[0] }))}
                onRemove={picked.weeklyService ? () => setPicked((p) => ({ ...p, weeklyService: null })) : undefined}
              />
            </Stack>
            {step === 'checking' && (
              <Box>
                <LinearProgress variant="determinate" value={(progress.done / Math.max(1, progress.total)) * 100} />
                <Typography variant="caption" color="text.secondary">Reading {progress.done} of {progress.total}</Typography>
              </Box>
            )}
          </Stack>
        )}

        {(step === 'check' || step === 'saving') && (
          <Stack gap={2}>
            {/* One picker for every row; serviceFor says whose it is. */}
            <input
              ref={serviceInput} type="file" hidden
              accept=".pdf,.csv,.xlsx,.xls,.jpg,.jpeg,.png,.heic"
              onChange={(e) => {
                const picked = e.target.files?.[0] || null;
                if (picked && serviceFor) setFiles((fs) => fs.map((x) => (x.key === serviceFor ? { ...x, weeklyService: picked } : x)));
                setServiceFor(null);
                e.target.value = '';
              }}
            />
            {/* The weeks */}
            <Card sx={{ overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Week</TableCell>
                      <TableCell align="right">Net amount due</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Stops</TableCell>
                      <TableCell>Saving</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plan.map(({ file: f, action }) => (
                      <TableRow
                        key={f.key}
                        onDragOver={(action === 'new' || action === 'replace') && step !== 'saving' ? acceptDrag : undefined}
                        onDrop={(action === 'new' || action === 'replace') && step !== 'saving' ? (e) => {
                          e.preventDefault();
                          const dropped = e.dataTransfer.files[0];
                          if (dropped) setFiles((fs) => fs.map((x) => (x.key === f.key ? { ...x, weeklyService: dropped } : x)));
                        } : undefined}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.preview ? weekLabel(f.preview.week.weekStart, f.preview.week.weekEnd) : f.fileName}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{f.preview ? f.fileName : f.error}</Typography>
                          {(action === 'new' || action === 'replace') && (
                            f.weeklyService ? (
                              <Stack direction="row" alignItems="center" gap={0.5}>
                                <AttachIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" noWrap>Weekly service: {f.weeklyService.name}</Typography>
                                <IconButton
                                  size="small" aria-label={`Remove the weekly service from ${f.fileName}`} disabled={step === 'saving'} sx={{ p: 0.25 }}
                                  onClick={() => setFiles((fs) => fs.map((x) => (x.key === f.key ? { ...x, weeklyService: null } : x)))}
                                >
                                  <RemoveIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Stack>
                            ) : (
                              <Button
                                size="small" startIcon={<AttachIcon sx={{ fontSize: 14 }} />} disabled={step === 'saving'}
                                onClick={() => { setServiceFor(f.key); serviceInput.current?.click(); }}
                                sx={{ p: 0, minWidth: 0, fontSize: 12, fontWeight: 500 }}
                              >
                                Attach or drop weekly service
                              </Button>
                            )
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{f.preview ? money(f.preview.week.totals.netAmountDue, { cents: true }) : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', display: { xs: 'none', sm: 'table-cell' } }}>
                          {f.preview ? (
                            <Box component="span" sx={{ color: f.preview.week.reconciliation.ok ? undefined : 'warning.dark' }} title={f.preview.week.reconciliation.ok ? 'Driver rows add up' : `Driver rows add up to ${count(f.preview.week.reconciliation.driverStops)}`}>
                              {count(f.preview.week.totals.totalStops)}{f.preview.week.reconciliation.ok ? '' : ' *'}
                            </Box>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <ToneChip tone={ACTION[action].tone} label={ACTION[action].label} />
                            {f.preview?.existing?.status === 'finalized' && (action === 'finalized' || f.replace) && (
                              <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                                <Checkbox size="small" checked={f.replace} onChange={(e) => setFiles((fs) => fs.map((x) => (x.key === f.key ? { ...x, replace: e.target.checked } : x)))} sx={{ p: 0.5 }} />
                                <Typography variant="caption">Replace</Typography>
                              </Box>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {plan.some((p) => p.file.preview && !p.file.preview.week.reconciliation.ok) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>* The driver rows do not add up to the statement&apos;s stops; the week still saves and says so.</Typography>
              )}
              {plan.some((p) => p.action === 'replace') && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pb: 1 }}>Replacing a draft keeps what was typed into it.</Typography>
              )}
            </Card>

            {/* The people */}
            {toSave.length > 0 && (
              <Card sx={{ overflow: 'hidden' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      {unknown.length ? `${unknown.length} ${unknown.length === 1 ? 'driver is' : 'drivers are'} not in the company` : 'Every driver matched'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{matched.length} matched by FedEx ID</Typography>
                  </Box>
                  {undecided.length > 0 && (
                    <Stack direction="row" gap={1} alignItems="center">
                      <Select size="small" displayEmpty value={bulkSetup} onChange={(e) => setBulkSetup(String(e.target.value))} sx={{ minWidth: 180 }} renderValue={(v) => (v ? setups.find((x) => x.value === v)?.label : 'Pay: set up later')}>
                        <MenuItem value="">Set up pay later</MenuItem>
                        {setups.map((x) => <MenuItem key={x.value} value={x.value}>{x.label}</MenuItem>)}
                      </Select>
                      <Button size="small" variant="outlined" onClick={() => undecided.forEach((u) => decide(u.fedexId, newDecision(u, 'former', bulkSetup)))} sx={{ whiteSpace: 'nowrap' }}>
                        Add the {undecided.length} left as former drivers
                      </Button>
                    </Stack>
                  )}
                </Stack>
                {unknown.map((u) => {
                  const d = decisions[u.fedexId];
                  const value = !d ? '' : d.kind === 'skip' ? 'skip' : d.kind === 'new' ? 'new' : d.accountId;
                  return (
                    <Box key={u.fedexId} sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', boxShadow: !d ? `inset 3px 0 0 ${statusTokens.warn}` : 'none' }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1.5}>
                        <Stack direction="row" alignItems="center" gap={1.25} sx={{ flex: 1, minWidth: 0 }}>
                          <Avatar name={u.name} size={28} muted />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              FedEx {u.fedexId} · {count(u.stops)} stops{toSave.length > 1 ? ` · in ${u.weeks} of ${toSave.length} weeks` : ''}
                            </Typography>
                          </Box>
                        </Stack>
                        <Select
                          size="small"
                          displayEmpty
                          value={value}
                          onChange={(e) => {
                            const v = String(e.target.value);
                            if (v === 'new') decide(u.fedexId, newDecision(u));
                            else if (v === 'skip') decide(u.fedexId, { kind: 'skip' });
                            else if (v) decide(u.fedexId, { kind: 'match', accountId: v, name: everyone.find((p) => p.accountId === v)?.name || '' });
                          }}
                          sx={{ width: { xs: '100%', sm: 240 } }}
                          inputProps={{ 'aria-label': `Who is ${u.name}?` }}
                          renderValue={(v) => {
                            if (!v) return <Typography component="span" color="text.secondary">Who is this?</Typography>;
                            if (v === 'skip') return 'Skip';
                            if (v === 'new') return 'Add as a new person';
                            return d?.kind === 'match' ? d.name : v;
                          }}
                        >
                          <MenuItem value="new">Add as a new person</MenuItem>
                          {u.suggestions.some((s) => s.likely) && <ListSubheader>Likely</ListSubheader>}
                          {u.suggestions.filter((s) => s.likely).map((s) => <MenuItem key={s.accountId} value={s.accountId}>{s.name}</MenuItem>)}
                          <ListSubheader>Everyone without a FedEx ID</ListSubheader>
                          {everyone.filter((p) => !u.suggestions.some((s) => s.likely && s.accountId === p.accountId)).map((p) => <MenuItem key={p.accountId} value={p.accountId}>{p.name}</MenuItem>)}
                          <ListSubheader>Or</ListSubheader>
                          <MenuItem value="skip">Skip (not paid in these weeks)</MenuItem>
                        </Select>
                      </Stack>
                      {d?.kind === 'new' && (
                        <Box sx={{ mt: 1.5, ml: { sm: 5 }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
                          <TextField size="small" label="First name" value={d.firstName} onChange={(e) => patchNew(u.fedexId, { firstName: e.target.value })} error={!d.firstName.trim()} />
                          <TextField size="small" label="Last name" value={d.lastName} onChange={(e) => patchNew(u.fedexId, { lastName: e.target.value })} error={!d.lastName.trim()} />
                          <ToggleButtonGroup size="small" exclusive value={d.status} onChange={(_, v) => v && patchNew(u.fedexId, { status: v })} sx={{ '& .MuiToggleButton-root': { flex: 1, py: 0.6 } }}>
                            <ToggleButton value="former">Former driver</ToggleButton>
                            <ToggleButton value="current">Current driver</ToggleButton>
                          </ToggleButtonGroup>
                          <Select size="small" displayEmpty value={d.setup} onChange={(e) => patchNew(u.fedexId, { setup: String(e.target.value) })} renderValue={(v) => (v ? `Pay: ${setups.find((x) => x.value === v)?.label}` : 'Pay: set up later')}>
                            <MenuItem value="">Set up pay later</MenuItem>
                            {templates.saved.length > 0 && <ListSubheader>Saved templates</ListSubheader>}
                            {setups.filter((x) => x.value.startsWith('t:')).map((x) => <MenuItem key={x.value} value={x.value}>{x.label}</MenuItem>)}
                            {templates.common.length > 0 && <ListSubheader>Common setups in this company</ListSubheader>}
                            {setups.filter((x) => x.value.startsWith('c:')).map((x) => <MenuItem key={x.value} value={x.value} sx={{ whiteSpace: 'normal' }}>{x.label}</MenuItem>)}
                          </Select>
                          {d.status === 'current' && (
                            <>
                              <TextField size="small" label="Email (to invite them)" value={d.email} onChange={(e) => patchNew(u.fedexId, { email: e.target.value })} />
                              <TextField size="small" label="Phone" value={d.phone} onChange={(e) => patchNew(u.fedexId, { phone: e.target.value })} />
                            </>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                            {d.status === 'former'
                              ? 'Kept for payroll and records: no login, no invitation, no paperwork reminders.'
                              : d.email.trim() && d.phone.trim()
                                ? 'They get the usual invitation by email and text.'
                                : 'Without an email and phone they are added as not yet invited; invite them from Users later.'}
                            {' '}Pay can be set or changed later in Driver pay, and fills in these weeks.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
                {matched.length > 0 && (
                  <Box sx={{ px: 2, py: 1 }}>
                    <Button size="small" onClick={() => setShowMatched((v) => !v)} endIcon={<ExpandIcon sx={{ transform: showMatched ? 'rotate(180deg)' : 'none' }} />} sx={{ color: 'text.secondary' }}>
                      {showMatched ? 'Hide' : 'Show'} the {matched.length} matched
                    </Button>
                    <Collapse in={showMatched}>
                      <Stack gap={0.5} sx={{ py: 1 }}>
                        {matched.map((m) => (
                          <Stack key={m.fedexId} direction="row" alignItems="center" gap={1}>
                            <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="body2">{m.name}</Typography>
                            {m.statementName.toLowerCase() !== m.name.toLowerCase() && <Typography variant="caption" color="text.secondary">on the statement as {m.statementName}</Typography>}
                          </Stack>
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                )}
              </Card>
            )}

            {files.length === 1 && files[0].preview && files[0].preview.absent.length > 0 && (
              <Card sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>Not in this statement</Typography>
                <Typography variant="caption" color="text.secondary">{files[0].preview.absent.map((a) => a.name).join(', ')}</Typography>
              </Card>
            )}

            {step === 'saving' && (
              <Box>
                <LinearProgress variant="determinate" value={(progress.done / Math.max(1, progress.total)) * 100} />
                <Typography variant="caption" color="text.secondary">{progress.label}</Typography>
              </Box>
            )}
          </Stack>
        )}

        {step === 'done' && (
          <Stack gap={1}>
            {results.map((r) => (
              <Stack key={r.fileName} direction="row" alignItems="center" gap={1.25}>
                <ToneChip tone={r.detail ? 'ok' : 'danger'} label={r.detail ? 'Saved' : 'Failed'} />
                <Typography variant="body2">{r.weekEnd ? `Week ending ${shortDate(r.weekEnd)}` : r.fileName}</Typography>
                {r.error && <Typography variant="caption" color="error.main">{r.error}</Typography>}
                {r.filesError && <Typography variant="caption" color="warning.dark">Pay saved; files not filed: {r.filesError}</Typography>}
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {step === 'check' && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {toSave.length} {toSave.length === 1 ? 'week' : 'weeks'} to save
            {undecided.length > 0 && ` · ${undecided.length} undecided ${undecided.length === 1 ? 'driver' : 'drivers'} will be skipped`}
          </Typography>
        )}
        {step === 'pick' && (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="contained" disableElevation disabled={!picked.statements.length} onClick={() => handleFiles(picked)}>
              Continue
            </Button>
          </>
        )}
        {step === 'pick' || step === 'checking' ? null : step === 'done' ? (
          <Button variant="contained" disableElevation onClick={finish}>{newest ? 'Open the newest week' : 'Close'}</Button>
        ) : (
          <>
            <Button onClick={close} disabled={step === 'saving'}>Cancel</Button>
            {(step === 'check' || step === 'saving') && (
              <Button variant="contained" disableElevation onClick={save} disabled={step === 'saving' || !toSave.length || incompleteNew.length > 0}>
                {toSave.length > 1 ? `Save ${toSave.length} weeks` : 'Save and calculate pay'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadStatementDialog;
